import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ArrowLeft, Lock } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import Dashboard from './admin/Dashboard';
import ProjectsManager from './admin/ProjectsManager';
import CategoriesManager from './admin/CategoriesManager';
import MediaLibrary from './admin/MediaLibrary';
import SettingsManager from './admin/SettingsManager';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function AdminPanel() {
  const navigate = useNavigate();
  const [authenticated, setAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('dashboard');
  const [stats, setStats] = useState({ total_projects: 0, total_images: 0, total_categories: 0 });
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('admin_auth');
    if (savedAuth) {
      setAuthenticated(true);
      fetchStats();
    }
  }, []);

  useEffect(() => {
    if (authenticated) {
      fetchStats();
    }
  }, [refreshTrigger, authenticated]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      const response = await axios.post(`${API}/auth/login?password=${encodeURIComponent(password)}`);
      if (response.data.success) {
        sessionStorage.setItem('admin_auth', 'true');
        sessionStorage.setItem('admin_password', password);
        setAuthenticated(true);
        toast.success('Login successful');
      }
    } catch (error) {
      toast.error('Invalid password');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    sessionStorage.removeItem('admin_auth');
    sessionStorage.removeItem('admin_password');
    setAuthenticated(false);
    setPassword('');
  };

  const fetchStats = async () => {
    try {
      const res = await axios.get(`${API}/stats`);
      setStats(res.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const triggerRefresh = () => {
    setRefreshTrigger(prev => prev + 1);
  };

  // Login screen
  if (!authenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 flex items-center justify-center p-6">
        <Card className="w-full max-w-md p-8 shadow-2xl">
          <div className="text-center mb-8">
            <div className="w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center" style={{ background: '#b8d71b' }}>
              <Lock size={32} className="text-gray-900" />
            </div>
            <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Admin Login</h1>
            <p className="text-gray-500">Enter your password to access the admin panel</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <Input
                type="password"
                placeholder="Enter admin password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="text-center text-lg h-12"
                required
                autoFocus
                data-testid="admin-password-input"
              />
            </div>
            <Button
              type="submit"
              className="w-full h-12 text-lg btn-hover"
              style={{ background: '#b8d71b', color: '#1a1a1a' }}
              disabled={loading}
              data-testid="admin-login-btn"
            >
              {loading ? 'Authenticating...' : 'Login'}
            </Button>
          </form>

          <div className="mt-6 text-center">
            <Button variant="ghost" onClick={() => navigate('/')} data-testid="back-to-site-btn">
              <ArrowLeft size={16} className="mr-2" />
              Back to Portfolio
            </Button>
          </div>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50" data-testid="admin-panel">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 sticky top-0 z-30">
        <div className="max-w-[1600px] mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Button
                onClick={() => navigate('/')}
                variant="ghost"
                size="icon"
                data-testid="back-to-portfolio-btn"
              >
                <ArrowLeft size={20} />
              </Button>
              <div>
                <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  Admin Panel
                </h1>
                <p className="text-sm text-gray-500">Manage your architecture portfolio</p>
              </div>
            </div>
            <div className="flex items-center gap-4">
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Projects</p>
                <p className="text-lg font-bold" style={{ color: '#b8d71b' }}>{stats.total_projects}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-gray-500">Total Images</p>
                <p className="text-lg font-bold" style={{ color: '#b8d71b' }}>{stats.total_images}</p>
              </div>
              <Button onClick={handleLogout} variant="outline" size="sm" data-testid="logout-btn">
                Logout
              </Button>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <div className="max-w-[1600px] mx-auto px-6 py-8">
        <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
          <TabsList className="grid w-full max-w-2xl grid-cols-5 mx-auto">
            <TabsTrigger value="dashboard" data-testid="tab-dashboard">Dashboard</TabsTrigger>
            <TabsTrigger value="projects" data-testid="tab-projects">Projects</TabsTrigger>
            <TabsTrigger value="media" data-testid="tab-media">Media</TabsTrigger>
            <TabsTrigger value="categories" data-testid="tab-categories">Categories</TabsTrigger>
            <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
          </TabsList>

          <TabsContent value="dashboard">
            <Dashboard stats={stats} onRefresh={triggerRefresh} />
          </TabsContent>

          <TabsContent value="projects">
            <ProjectsManager onRefresh={triggerRefresh} />
          </TabsContent>

          <TabsContent value="media">
            <MediaLibrary onRefresh={triggerRefresh} />
          </TabsContent>

          <TabsContent value="categories">
            <CategoriesManager onRefresh={triggerRefresh} />
          </TabsContent>

          <TabsContent value="settings">
            <SettingsManager onRefresh={triggerRefresh} />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}